import useProfile from "@/hooks/useProfile";
import { CASE_MEMBER_TYPE } from "@/libraries/constant";
import { getCaseAskHemiProfileQueryOptions } from "@/queries";
import type { Case } from "@/types";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { memo } from "react";
import { useTranslation } from "react-i18next";

interface CaseAskHemiProfileCardProps {
	caseItem: Case;
}

const formatHba1c = (raw: unknown): string => {
	if (!raw) return "-";
	if (typeof raw === "object" && raw !== null) {
		const obj = raw as { value?: string | number; unit?: string };
		if (obj.value == null || obj.value === "") return "-";
		const valStr = String(obj.value).trim();
		if (!valStr) return "-";
		if (obj.unit) return `${valStr} ${obj.unit}`;
		return valStr;
	}
	const str = String(raw).trim();
	return str || "-";
};

const formatBp = (raw: unknown): string => {
	if (!raw) return "-";
	if (typeof raw === "object" && raw !== null) {
		const obj = raw as {
			systolic?: number | string;
			diastolic?: number | string;
			value?: string | number;
		};
		if (obj.systolic && obj.diastolic)
			return `${obj.systolic}/${obj.diastolic}`;
		if (obj.value != null && obj.value !== "") return String(obj.value).trim();
		return "-";
	}
	const str = String(raw).trim();
	return str || "-";
};

const formatBmi = (raw: unknown): string => {
	if (raw == null || raw === "") return "-";
	if (typeof raw === "object" && raw !== null) {
		const obj = raw as { value?: string | number };
		if (obj.value != null && obj.value !== "") return String(obj.value).trim();
		return "-";
	}
	const str = String(raw).trim();
	return str || "-";
};

const formatFamilyHistoryItem = (item: unknown): string => {
	if (!item) return "";
	if (typeof item === "string") return item.trim();
	if (typeof item === "object" && item !== null) {
		const f = item as {
			rel?: string;
			relation?: string;
			condition?: string;
			age_at_diagnosis?: number | string;
			age?: number | string;
			status?: string;
		};
		const rel = (f.rel || f.relation || "").trim();
		const condition = (f.condition || "").trim();
		const age =
			f.age_at_diagnosis != null && f.age_at_diagnosis !== ""
				? f.age_at_diagnosis
				: f.age;
		const parts: string[] = [];
		if (rel && condition) {
			parts.push(`${rel} ${condition}`);
		} else if (condition) {
			parts.push(condition);
		} else if (rel) {
			parts.push(rel);
		}
		if (age != null && age !== "") {
			parts.push(`at ${age}`);
		}
		return parts.join(" ");
	}
	return String(item).trim();
};

const CaseAskHemiProfileCard = ({ caseItem }: CaseAskHemiProfileCardProps) => {
	const { t } = useTranslation("askhemi");
	const { profile } = useProfile();

	const isExternal =
		caseItem.member_type === CASE_MEMBER_TYPE.EXTERNAL_REQUEST_MEMBER ||
		Boolean(
			profile && caseItem.external_members?.some((m) => m.id === profile.id),
		);

	const profileQuery = useQuery(
		getCaseAskHemiProfileQueryOptions(caseItem.id, {
			enabled: !isExternal && !!caseItem.id,
		}),
	);

	if (isExternal || profileQuery.isLoading || profileQuery.isError) {
		return null;
	}

	const data = profileQuery.data;
	if (!data?.available) {
		return null;
	}

	const vitals = data.vitals ?? {};
	const familyHistoryItems = Array.isArray(data.family_history)
		? data.family_history.map(formatFamilyHistoryItem).filter(Boolean)
		: [];
	const familyHistoryFromApi =
		familyHistoryItems.length > 0 ? familyHistoryItems.join(", ") : "";

	const hba1c = formatHba1c(vitals.hba1c);
	const bp = formatBp(vitals.blood_pressure);
	const bmi = formatBmi(vitals.bmi);
	const familyHx = familyHistoryFromApi || "-";

	const shareDate = data.shared_at || data.updated_at;
	const updatedAt = shareDate
		? dayjs(shareDate).format("D MMM YYYY")
		: dayjs().format("D MMM YYYY");

	return (
		<div className="rounded-xl border border-zinc-200/80 bg-white p-3.5 shadow-sm">
			<div className="flex items-center justify-between mb-2.5">
				<span className="block text-xs text-zinc-400">
					{t("profileCard.subtitle")}
				</span>
				{data.share_mode === "ongoing" ? (
					<span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-medium text-emerald-700 border border-emerald-200">
						Ongoing access
					</span>
				) : (
					<span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10.5px] font-medium text-zinc-600">
						Share once
					</span>
				)}
			</div>
			<div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-3">
				<div>
					<span className="block text-[11.5px] text-zinc-400">
						{t("profileCard.hba1c")}
					</span>
					<b className="text-[14px] font-bold text-zinc-900">{hba1c}</b>
				</div>
				<div>
					<span className="block text-[11.5px] text-zinc-400">
						{t("profileCard.bp")}
					</span>
					<b className="text-[14px] font-bold text-zinc-900">{bp}</b>
				</div>
				<div>
					<span className="block text-[11.5px] text-zinc-400">
						{t("profileCard.bmi")}
					</span>
					<b className="text-[14px] font-bold text-zinc-900">{bmi}</b>
				</div>
				<div>
					<span className="block text-[11.5px] text-zinc-400">
						{t("profileCard.familyHx")}
					</span>
					<b className="text-[14px] font-bold text-zinc-900">{familyHx}</b>
				</div>
			</div>
			<span className="inline-flex items-center gap-1.5 rounded-full bg-[#e7f1fb] px-2.5 py-0.5 text-[10.5px] font-semibold text-[#1a6ca8]">
				{t("profileCard.enteredByPatientUpdated", { date: updatedAt })}
			</span>
		</div>
	);
};

export default memo(CaseAskHemiProfileCard);
